import { useNavigate, useSearch } from "@tanstack/react-router";
import {
  cloneDeep,
  isEqual,
  get as lodashGet,
  set as lodashSet,
  unset as lodashUnset,
} from "lodash-es";
import { useCallback, useRef } from "react";

// Stabilize a value across renders by deep equality. Prevents inline
// `{ defaultValue: [] }` / `{ defaultValue: {} }` from churning setter
// identity on every render.
function useStable<T>(value: T): T {
  const ref = useRef(value);
  if (!isEqual(ref.current, value)) ref.current = value;
  return ref.current;
}

type History = "push" | "replace";

type SetterOptions = {
  history?: History;
  clearKeys?: string[];
};

type Updater<T> = (prev: T) => T | null;

type Setter<T> = (
  next: T | null | Updater<T>,
  options?: SetterOptions,
) => Promise<void>;

type QueryStateOptions<T> = {
  defaultValue?: T;
  clearOnDefault?: boolean;
  history?: History;
};

type QueryStateResult<T, O extends QueryStateOptions<T>> = O extends {
  defaultValue: T;
}
  ? [T, Setter<T>]
  : [T | null, Setter<T>];

type ArrayQueryStateOptions = {
  array: true;
  defaultValue?: string[];
  clearOnDefault?: boolean;
  history?: History;
};

/**
 * Normalize whatever lodash returned for a list-shaped key into a `string[]`.
 * Handles every shape `parseSearchString` can produce:
 *   - `undefined` / `null` / `""` → defaultValue (caller-provided fallback)
 *   - `"a,b"` (canonical comma string) → `["a","b"]`
 *   - `["a","b"]` (legacy indexed-bracket bookmark) → kept as-is
 *   - any other shape → defaultValue
 */
function toStringArray(raw: unknown, defaultValue: string[]): string[] {
  if (raw == null || raw === "") return defaultValue;
  if (Array.isArray(raw))
    return raw.filter((v): v is string => typeof v === "string");
  if (typeof raw === "string")
    return raw
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);
  return defaultValue;
}

// Array overload: `{ array: true }` flips read/write to comma-list mode and
// pins T to `string[]`. Always returns a non-null array.
export function useQueryState(
  key: string,
  options: ArrayQueryStateOptions,
): [string[], Setter<string[]>];

// No-default overload: value is nullable; `T` is explicit or defaults to string.
// Accepts option-only calls — `useQueryState<OrdersLayout>("layout", { history: "push" })`.
export function useQueryState<T = string>(
  key: string,
  options?: Omit<QueryStateOptions<T>, "defaultValue">,
): [T | null, Setter<T>];

// defaultValue overload: `T` infers from the provided default and the value is
// non-null — `useQueryState("hide", { defaultValue: "" })` → [string, Setter<string>].
// (In the `<T, O>` overload below, T has no inference site of its own, so
// option-only calls would silently land on `unknown` without this one.)
export function useQueryState<T>(
  key: string,
  options: QueryStateOptions<T> & { defaultValue: T },
): [T, Setter<T>];

export function useQueryState<T, O extends QueryStateOptions<T>>(
  key: string,
  options: O,
): QueryStateResult<T, O>;

export function useQueryState<T = string>(
  key: string,
  options: QueryStateOptions<T> | ArrayQueryStateOptions = {},
) {
  const isArray = (options as ArrayQueryStateOptions).array === true;
  const { clearOnDefault = true, history = "replace" } = options;
  const defaultValue = useStable(
    isArray
      ? ((options.defaultValue as string[] | undefined) ?? [])
      : (options.defaultValue as T | undefined),
  );

  const navigate = useNavigate();

  const value = (useSearch as any)({
    strict: false,
    select: (s: unknown) => {
      const raw = lodashGet(s as object, key);
      if (isArray) return toStringArray(raw, defaultValue as string[]);
      return raw === undefined ? (defaultValue ?? null) : (raw as T);
    },
    structuralSharing: true,
  });

  const setValue = useCallback<Setter<any>>(
    (next, opts) => {
      return navigate({
        to: ".",
        search: (prev) => {
          const draft = cloneDeep(prev) as Record<string, unknown>;
          const prevRaw = lodashGet(draft, key);
          const prevResolved = isArray
            ? toStringArray(prevRaw, defaultValue as string[])
            : prevRaw === undefined
              ? (defaultValue ?? null)
              : prevRaw;

          const resolved =
            typeof next === "function"
              ? (next as Updater<any>)(prevResolved)
              : next;

          const equalsDefault =
            defaultValue !== undefined && isEqual(resolved, defaultValue);
          const isEmptyArray =
            isArray && Array.isArray(resolved) && resolved.length === 0;
          const strip =
            resolved === null ||
            isEmptyArray ||
            (clearOnDefault && equalsDefault);

          if (strip) {
            lodashUnset(draft, key);
          } else {
            lodashSet(draft, key, resolved);
          }

          for (const k of opts?.clearKeys ?? []) {
            lodashUnset(draft, k);
          }

          if (isEqual(prev, draft)) return prev as Record<string, unknown>;
          return draft;
        },
        replace: (opts?.history ?? history) === "replace",
      }) as Promise<void>;
    },
    [navigate, key, defaultValue, clearOnDefault, history, isArray],
  );

  return [value, setValue] as const;
}

type QueryStatesConfig = Record<string, QueryStateOptions<unknown>>;

type QueryStatesValue<C extends QueryStatesConfig> = {
  [K in keyof C]: C[K] extends { defaultValue: infer D }
    ? D
    : C[K] extends QueryStateOptions<infer T>
      ? T | null
      : never;
};

type QueryStatesSetter<C extends QueryStatesConfig> = (
  next:
    | Partial<{ [K in keyof C]: QueryStatesValue<C>[K] | null }>
    | ((prev: QueryStatesValue<C>) => Partial<{
        [K in keyof C]: QueryStatesValue<C>[K] | null;
      }>),
  options?: SetterOptions,
) => Promise<void>;

export function useQueryStates<C extends QueryStatesConfig>(
  configInput: C,
): [QueryStatesValue<C>, QueryStatesSetter<C>] {
  const config = useStable(configInput);
  const navigate = useNavigate();

  const value = (useSearch as any)({
    strict: false,
    select: (s: unknown) => {
      const out = {} as QueryStatesValue<C>;
      for (const key in config) {
        const { defaultValue } = config[key]!;
        const raw = lodashGet(s as object, key);
        (out as Record<string, unknown>)[key] =
          raw === undefined ? (defaultValue ?? null) : raw;
      }
      return out;
    },
    structuralSharing: true,
  }) as QueryStatesValue<C>;

  const setValues = useCallback<QueryStatesSetter<C>>(
    (next, opts) => {
      return navigate({
        to: ".",
        search: (prev) => {
          const draft = cloneDeep(prev) as Record<string, unknown>;

          const prevResolved = {} as QueryStatesValue<C>;
          for (const key in config) {
            const { defaultValue } = config[key]!;
            const raw = lodashGet(draft, key);
            (prevResolved as Record<string, unknown>)[key] =
              raw === undefined ? (defaultValue ?? null) : raw;
          }

          const patch =
            typeof next === "function"
              ? (next as (p: QueryStatesValue<C>) => Record<string, unknown>)(
                  prevResolved,
                )
              : next;

          for (const key in patch) {
            const resolved = (patch as Record<string, unknown>)[key];
            const { defaultValue, clearOnDefault = true } = config[key] ?? {};
            const equalsDefault =
              defaultValue !== undefined && isEqual(resolved, defaultValue);
            const strip =
              resolved === null || (clearOnDefault && equalsDefault);

            if (strip) lodashUnset(draft, key);
            else lodashSet(draft, key, resolved);
          }

          for (const k of opts?.clearKeys ?? []) {
            lodashUnset(draft, k);
          }

          if (isEqual(prev, draft)) return prev as Record<string, unknown>;
          return draft;
        },
        replace: (opts?.history ?? "replace") === "replace",
      }) as Promise<void>;
    },
    [navigate, config],
  );

  return [value, setValues];
}
